import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? match[1] : null;

    const run = async () => {
      if (!sessionId) {
        navigate("/login", { replace: true });
        return;
      }
      const exchange = async () => {
        // Retry transient origin/proxy failures (5xx, network, Cloudflare 52x) — a 401 is final.
        for (let attempt = 0; ; attempt++) {
          try { return await api.post("/auth/session", { session_id: sessionId }); }
          catch (e) {
            const st = e?.response?.status;
            if (attempt >= 2 || (st && st < 500)) throw e;
            await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
          }
        }
      };
      try {
        const { data } = await exchange();
        // /auth/session returns the full profile (access levels included); refresh from /auth/me as a safety net
        setUser(data.module_levels ? data : (await api.get("/auth/me")).data);
        window.history.replaceState({}, document.title, "/");
        navigate("/", { replace: true });
      } catch (e) {
        // A reload/tab-restore of the callback URL re-uses a one-time session_id. If the
        // cookie session was already established, continue instead of bouncing to login.
        try {
          const me = await api.get("/auth/me");
          setUser(me.data);
          window.history.replaceState({}, document.title, "/");
          navigate("/", { replace: true });
          return;
        } catch { /* fall through */ }
        const st = e?.response?.status;
        const detail = e?.response?.data?.detail;
        const fallback = !st || st >= 500 ? "The server was briefly unavailable while signing you in. Please click Continue with Google again." : "Sign-in could not be completed. Please try again.";
        sessionStorage.setItem("auth_error", typeof detail === "string" ? detail : fallback);
        navigate("/login", { replace: true });
      }
    };
    run();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="font-mono text-sm text-muted-foreground animate-pulse">Authenticating…</div>
    </div>
  );
}
